/**
 * The step between "signed in" and "in an organization" — and the reason
 * `x-packagepage-org` is never guessed.
 *
 * Every org-scoped route on the API refuses a request that does not name its
 * organization, and **nothing in the product told the SPA how to populate that header
 * until this commit** (F4). `orgs.mine` is the query that answers it: it is keyed on the
 * session's user id, it is the one read that is not org-scoped, and its result is the
 * complete set of values the header may legally take.
 *
 * Three outcomes, and each is a screen rather than an error:
 *
 *   - **No memberships** → the bootstrap form. A brand name and nothing else
 *     (`screens.md` §1: *"Prefix entry is deliberately not step 1"*).
 *   - **One membership** → straight through, header set, no chooser. The private beta's
 *     organizations are single-user and a picker with one option is a click that teaches
 *     nothing.
 *   - **Several** → a minimal picker. The API permits a second organization; the UI does
 *     not offer creating one in Phase 2, and that restraint is the UI's alone — the
 *     bootstrap route would accept it, which is asserted rather than assumed.
 *
 * A request that arrives with no header renders the picker rather than an error screen.
 * That is the case worth stating: `ORG_NOT_SPECIFIED` is a refusal the user can act on,
 * and the action is choosing.
 */
import { useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button, Input, Surface } from "@packagepage/ui";
import { createOrgSchema, orgSlug } from "@packagepage/shared";

import {
  getActiveOrg,
  isUnauthenticated,
  problemOf,
  setActiveOrg,
  subscribeActiveOrg,
} from "../api/client.js";
import { api } from "../api/procedures.js";

import "../routes/auth.css";

/** What the gate hands the shell once an organization is settled on. */
export interface ActiveOrg {
  readonly id: string;
  readonly name: string;
  readonly role: string;
}

/**
 * The bootstrap form — brand name only.
 *
 * The slug preview and the server's stored value come from **one** function,
 * `orgSlug` in `packages/shared`. Two spellings of that rule would disagree on the first
 * name carrying an apostrophe, and the user would meet the disagreement as a URL that is
 * not the one they were shown.
 *
 * `createOrgSchema.safeParse` runs here too — the same schema the procedure validates
 * with, so the form refuses what the server would refuse, in the server's own words. It
 * is not a second rule; it is the same rule, earlier.
 */
function Bootstrap({ onCreated }: { onCreated: (org: ActiveOrg) => void }): ReactNode {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (brandName: string) => api.createOrg({ name: brandName }),
    onSuccess: (org) => onCreated({ id: org.id, name: org.name, role: org.role }),
    onError: (failure) => {
      const problem = problemOf(failure);
      setError(
        problem?.detail ??
          "We could not create your organisation. Nothing was saved — try again.",
      );
    },
  });

  const slug = orgSlug(name);

  function submit() {
    setError(null);
    const parsed = createOrgSchema.safeParse({ name });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "That brand name is not valid.");
      return;
    }
    create.mutate(parsed.data.name);
  }

  return (
    <div className="pp-auth" data-screen-label="Create your organisation">
      <div className="pp-auth__panel">
        {/* The same card as `/signin` — these three screens share `.pp-auth`, so styling
            one of them and not the others would create the inconsistency the card was
            added to remove. */}
        <Surface className="pp-auth__card">
          <div>
            <div className="pp-auth__title">What is your brand called?</div>
            <p className="pp-auth__lede">
              This is the name shoppers see on your product pages. You can change it
              later. No card, and no GS1 paperwork yet.
            </p>
          </div>

          {error === null ? null : (
            <Surface tone="error" emphasis="filled">
              {error}
            </Surface>
          )}

          <form
            className="pp-auth__form"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <div className="pp-auth__field">
              <Input
                label="Brand name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="organization"
                required
              />
              <span
                className={
                  slug === "" ? "pp-auth__slug pp-auth__slug-empty" : "pp-auth__slug"
                }
              >
                {slug === "" ? "your-brand" : slug}
              </span>
            </div>

            <Button type="submit" variant="primary" loading={create.isPending}>
              {create.isPending ? "Setting things up…" : "Continue"}
            </Button>
          </form>
        </Surface>
      </div>
    </div>
  );
}

function Picker({
  orgs,
  onPick,
}: {
  orgs: readonly ActiveOrg[];
  onPick: (org: ActiveOrg) => void;
}): ReactNode {
  return (
    <div className="pp-auth" data-screen-label="Choose an organisation">
      <div className="pp-auth__panel">
        <Surface className="pp-auth__card">
          <div>
            <div className="pp-auth__title">Which organisation?</div>
            <p className="pp-auth__lede">
              You belong to more than one. Everything you do next applies to the one you
              pick.
            </p>
          </div>
          <div className="pp-auth__picker">
            {orgs.map((org) => (
              <Button key={org.id} onClick={() => onPick(org)}>
                {org.name} · {org.role}
              </Button>
            ))}
          </div>
        </Surface>
      </div>
    </div>
  );
}

export function OrgGate({
  children,
}: {
  children: (org: ActiveOrg) => ReactNode;
}): ReactNode {
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<ActiveOrg | null>(null);

  /**
   * The org header's current value, **subscribed to** rather than read during render.
   *
   * The gate below refuses to render children until the header names the organization
   * they are about to query for. Reading `getActiveOrg()` bare made that gate a read of
   * module state React knows nothing about, so the effect that sets it could not cause the
   * render that passes it — and on the one-membership path nothing else could either.
   * c37b's finding: the dashboard rendered an empty page for every signed-in user, and no
   * static-render test could see it because static rendering runs no effects.
   *
   * The third argument is the server snapshot, and it is not decoration: `apps/app`'s
   * suite is `renderToStaticMarkup` by decision (c34), and `useSyncExternalStore` throws
   * under static rendering without one.
   */
  const headerOrg = useSyncExternalStore(subscribeActiveOrg, getActiveOrg, getActiveOrg);

  const mine = useQuery({
    queryKey: ["orgs", "mine"],
    queryFn: () => api.myOrgs(),
  });

  const orgs = mine.data?.orgs ?? [];

  // The header follows the choice, in one place, and the effect is what keeps them from
  // drifting: a component that set the header itself would be a second writer, and
  // `client.ts` exists so there is exactly one.
  useEffect(() => {
    const only = orgs.length === 1 ? orgs[0] : undefined;
    const active = chosen ?? (only === undefined ? null : { ...only });
    if (active !== null && getActiveOrg() !== active.id) {
      setActiveOrg(active.id);
      // Anything already fetched was fetched for a different organization, or for none.
      void queryClient.invalidateQueries();
    }
  }, [chosen, orgs, queryClient]);

  if (mine.isPending) {
    return (
      <div className="pp-auth">
        <p className="pp-auth__lede">Checking your account&hellip;</p>
      </div>
    );
  }

  if (mine.error !== null) {
    if (isUnauthenticated(mine.error)) {
      window.location.assign("/signin");
      return null;
    }
    const problem = problemOf(mine.error);
    return (
      <div className="pp-auth">
        <div className="pp-auth__panel">
          <Surface tone="error" emphasis="filled">
            {problem?.detail ??
              "We could not reach your account. Your printed codes are unaffected — they resolve from the resolver's own mirror and do not depend on this screen."}
          </Surface>
        </div>
      </div>
    );
  }

  if (orgs.length === 0) {
    return (
      <Bootstrap
        onCreated={(org) => {
          setChosen(org);
          void queryClient.invalidateQueries({ queryKey: ["orgs", "mine"] });
        }}
      />
    );
  }

  const active = chosen ?? (orgs.length === 1 ? orgs[0] : undefined);
  if (active === undefined) {
    return <Picker orgs={orgs} onPick={setChosen} />;
  }

  // The header is set by the effect above; until it is, the children's queries would go
  // out unscoped and be refused. Rendering nothing for one tick is the honest wait — and
  // it is genuinely one tick now, because `headerOrg` is subscribed and the store notifies
  // when the effect writes it. It was not: this line used to read module state bare during
  // render, and the wait it described had no wake-up at all (c37b).
  if (headerOrg !== active.id) return null;

  return <>{children(active)}</>;
}
